import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useScrollTo } from "@/hooks/useScrollTo";
import { useLenis } from "lenis/react";

const LINK_IDS = [
  { key: "nav.mission", id: "mission" },
  { key: "nav.president", id: "president" },
  { key: "nav.galleries", id: "galleries" },
  { key: "nav.involved", id: "involved" },
];

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className="flex items-center border border-[#0A1128]/20">
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
            lang === l ? "bg-[#D4AF37] text-[#0A1128]" : "text-[#0A1128]/60 hover:text-[#0A1128]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = ({ onDonate }) => {
  const { t } = useLang();
  const scrollTo = useScrollTo();
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollTo(id); };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_20px_rgba(10,17,40,0.08)] border-b border-[#0A1128]/10" : "border-b border-[#0A1128]/5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => (lenis ? lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" }))}
          className="flex items-center"
        >
          <img src="/logo.png" alt="Focus Haitian Music Inc." className="h-16 md:h-20 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINK_IDS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-[#0A1128]/70 hover:text-[#B8901F] transition-colors duration-300"
            >
              {t(l.key)}
            </button>
          ))}
          <LangToggle />
          <button
            data-testid="nav-donate-button"
            onClick={onDonate}
            className="px-6 py-2.5 bg-[#D4AF37] text-[#0A1128] text-sm font-semibold hover:bg-[#E5C158] hover:scale-[1.03] transition-all duration-300"
          >
            {t("nav.donate")}
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="text-[#0A1128] text-xl">
            {open ? "×" : "≡"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-t border-[#0A1128]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINK_IDS.map((l) => (
                <button key={l.id} data-testid={`nav-mobile-${l.id}`} onClick={() => go(l.id)} className="text-left text-[#0A1128] text-lg font-display">
                  {t(l.key)}
                </button>
              ))}
              <button data-testid="nav-mobile-donate" onClick={() => { setOpen(false); onDonate(); }} className="mt-2 px-6 py-3 bg-[#D4AF37] text-[#0A1128] font-semibold">
                {t("nav.donate")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
